import React, { useState } from 'react';
import { ProductType, UserRole } from '../../types';
import { HumanRisksLogo } from './HumanRisksLogo';
import { ApiStatusIndicator } from './ApiStatusIndicator';

type Activable = ProductType | 'pipeline' | 'admin' | 'claims' | 'history';

interface SidebarProps {
  activeProduct: Activable;
  onSelect: (view: Activable) => void;
  userRole?: UserRole;
}

interface NavItemProps {
  id: Activable;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: string;
  isSubItem?: boolean;
  expanded: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  id: _id,
  label,
  active,
  onClick,
  badge,
  isSubItem,
  expanded,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center px-3 text-xs font-bold transition-colors rounded-lg mb-0.5 group relative ${
      active
        ? 'bg-hr-pink text-white shadow-lg'
        : 'text-slate-400 hover:bg-white/5 hover:text-white'
    } ${isSubItem ? 'py-1.5' : 'py-2.5'}`}
    aria-label={label}
  >
    <span className="shrink-0 w-2 h-2 rounded-full bg-current opacity-40" />
    {expanded && (
      <span className="flex items-center justify-between flex-1 ml-3 min-w-0">
        <span className="truncate">{label}</span>
        {badge && (
          <span
            className={`shrink-0 text-micro px-1.5 py-0.5 rounded font-black uppercase tracking-tighter ml-2 ${
              active ? 'bg-white/20 text-white' : 'bg-hr-pink/20 text-hr-pink'
            }`}
          >
            {badge}
          </span>
        )}
      </span>
    )}
    {!expanded && (
      <span className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-micro rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[60] font-bold uppercase tracking-wider shadow-xl border border-white/10">
        {label}
      </span>
    )}
  </button>
);

interface NavSectionProps {
  id: string;
  label: string;
  expanded: boolean;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const NavSection: React.FC<NavSectionProps> = ({
  id: _id,
  label,
  expanded,
  isOpen,
  onToggle,
  children,
}) => (
  <div className="mb-1">
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center px-3 py-2.5 text-xs font-bold transition-colors rounded-lg group relative ${
        isOpen && expanded
          ? 'text-white bg-white/5'
          : 'text-slate-400 hover:bg-white/5 hover:text-white'
      }`}
      aria-expanded={isOpen}
    >
      <span className="shrink-0 w-2 h-2 rounded-full bg-current opacity-40" />
      {expanded && (
        <span className="flex items-center justify-between flex-1 ml-3 min-w-0">
          <span className="truncate uppercase text-micro tracking-widest font-black">{label}</span>
          <span className={`text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>⌄</span>
        </span>
      )}
      {!expanded && (
        <span className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-micro rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[60] font-bold uppercase tracking-wider shadow-xl border border-white/10">
          {label}
        </span>
      )}
    </button>
    {isOpen && expanded && (
      <div className="overflow-hidden ml-2 border-l border-white/5 pl-2 mt-0.5 space-y-0.5">
        {children}
      </div>
    )}
  </div>
);

export const Sidebar: React.FC<SidebarProps> = ({ activeProduct, onSelect, userRole }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>('pa');

  const isInternal = userRole !== UserRole.Broker;

  const toggleSection = (section: string): void => {
    if (!isExpanded) {
      setIsExpanded(true);
      setOpenSection(section);
      return;
    }
    setOpenSection((prev) => (prev === section ? null : section));
  };

  return (
    <aside
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      className={`bg-hr-indigo h-screen sticky top-0 flex flex-col border-r border-white/5 z-50 overflow-y-auto no-scrollbar overflow-x-hidden shadow-2xl transition-[width] duration-200 ${
        isExpanded ? 'w-72' : 'w-[72px]'
      }`}
      aria-label="Primary navigation"
    >
      <div className={`p-6 mb-4 ${isExpanded ? '' : 'px-4'}`}>
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="shrink-0">
            <HumanRisksLogo variant="white" className="h-8" />
          </div>
          {isExpanded && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="h-1 w-8 bg-hr-pink rounded-full"></span>
                <span className="text-micro font-black text-white/40 uppercase tracking-widest whitespace-nowrap">
                  Workbench v1.0
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3">
        <NavItem
          id="pipeline"
          label="Unified Pipeline"
          active={activeProduct === 'pipeline'}
          onClick={() => onSelect('pipeline')}
          expanded={isExpanded}
          badge="v2"
        />

        <div className="h-px bg-white/5 my-4 mx-2" />

        <NavSection
          id="pa"
          label="Personal Accident"
          expanded={isExpanded}
          isOpen={openSection === 'pa'}
          onToggle={() => toggleSection('pa')}
        >
          <NavItem
            id={ProductType.CommercialPA}
            label="Commercial SB/GPA"
            isSubItem
            active={activeProduct === ProductType.CommercialPA}
            onClick={() => onSelect(ProductType.CommercialPA)}
            expanded={isExpanded}
          />
          <NavItem
            id={ProductType.CorporatePA}
            label="Corporate"
            isSubItem
            active={activeProduct === ProductType.CorporatePA}
            onClick={() => onSelect(ProductType.CorporatePA)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.FilmMediaPA}
            label="Film & Media"
            isSubItem
            active={activeProduct === ProductType.FilmMediaPA}
            onClick={() => onSelect(ProductType.FilmMediaPA)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.AviationPA}
            label="Aviation"
            isSubItem
            active={activeProduct === ProductType.AviationPA}
            onClick={() => onSelect(ProductType.AviationPA)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.ShortPeriodPA}
            label="Short Period"
            isSubItem
            active={activeProduct === ProductType.ShortPeriodPA}
            onClick={() => onSelect(ProductType.ShortPeriodPA)}
            expanded={isExpanded}
            badge="v2"
          />
        </NavSection>

        <NavSection
          id="travel"
          label="Travel Insurance"
          expanded={isExpanded}
          isOpen={openSection === 'travel'}
          onToggle={() => toggleSection('travel')}
        >
          <NavItem
            id={ProductType.BusinessTravel}
            label="Business Travel"
            isSubItem
            active={activeProduct === ProductType.BusinessTravel}
            onClick={() => onSelect(ProductType.BusinessTravel)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.OnceOffTravel}
            label="Once-Off Travel"
            isSubItem
            active={activeProduct === ProductType.OnceOffTravel}
            onClick={() => onSelect(ProductType.OnceOffTravel)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.InboundTravel}
            label="Inbound Scheme"
            isSubItem
            active={activeProduct === ProductType.InboundTravel}
            onClick={() => onSelect(ProductType.InboundTravel)}
            expanded={isExpanded}
            badge="v2"
          />
          <NavItem
            id={ProductType.LocalTravel}
            label="Local / Domestic"
            isSubItem
            active={activeProduct === ProductType.LocalTravel}
            onClick={() => onSelect(ProductType.LocalTravel)}
            expanded={isExpanded}
            badge="v2"
          />
        </NavSection>

        <NavSection
          id="medical"
          label="Medical Emergency"
          expanded={isExpanded}
          isOpen={openSection === 'medical'}
          onToggle={() => toggleSection('medical')}
        >
          <NavItem
            id={ProductType.MedicalEvacuation}
            label="Medical Evac"
            isSubItem
            active={activeProduct === ProductType.MedicalEvacuation}
            onClick={() => onSelect(ProductType.MedicalEvacuation)}
            expanded={isExpanded}
            badge="v3"
          />
        </NavSection>

        <NavSection
          id="knr"
          label="Kidnap & Ransom"
          expanded={isExpanded}
          isOpen={openSection === 'knr'}
          onToggle={() => toggleSection('knr')}
        >
          <NavItem
            id={ProductType.KidnapRansom}
            label="Kidnap & Ransom"
            isSubItem
            active={activeProduct === ProductType.KidnapRansom}
            onClick={() => onSelect(ProductType.KidnapRansom)}
            expanded={isExpanded}
            badge="v3"
          />
        </NavSection>

        <div className="h-px bg-white/5 my-4 mx-2" />

        <NavItem
          id="claims"
          label="Claims Workbench"
          active={activeProduct === 'claims'}
          onClick={() => onSelect('claims')}
          expanded={isExpanded}
          badge="v2"
        />

        <NavSection
          id="shared"
          label="Shared Services"
          expanded={isExpanded}
          isOpen={openSection === 'shared'}
          onToggle={() => toggleSection('shared')}
        >
          <NavItem
            id="history"
            label="Quote History"
            isSubItem
            active={activeProduct === 'history'}
            onClick={() => onSelect('history')}
            expanded={isExpanded}
          />
          {isInternal && (
            <NavItem
              id="admin"
              label="Admin Console"
              isSubItem
              active={activeProduct === 'admin'}
              onClick={() => onSelect('admin')}
              expanded={isExpanded}
            />
          )}
        </NavSection>
      </nav>

      <div className="p-4 border-t border-white/5 bg-black/20 space-y-3">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="h-8 w-8 bg-hr-pink rounded-full flex items-center justify-center font-bold text-micro text-white shrink-0">
            {userRole?.charAt(0) ?? '?'}
          </div>
          {isExpanded && (
            <div className="flex-1 min-w-0">
              <p className="text-micro font-bold text-white truncate">Underwriter Session</p>
              <p className="text-micro text-slate-500 uppercase tracking-wider">{userRole}</p>
            </div>
          )}
        </div>
        <ApiStatusIndicator expanded={isExpanded} />
      </div>
    </aside>
  );
};
